import { useQuery } from "@tanstack/react-query";
import { AxiosError } from 'axios';
import { clientsUsageApi } from "./axios";
import {
    ServiceResponseI,
    ClientUsageI,
    ClientUsageLimitsI,
    ContainerUsagePageI,
    ContainerGroupedUsagePageI,
    ClientUsageProcessT,
} from '../../interfaces';
import { ValidateCurrentToken, LogoutSystem } from '../../helpers';

const handleError = (err: unknown): never => {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
        console.error(obj.statusText);
        LogoutSystem();
    }
    throw new Error((obj?.data as Error)?.message ?? (err as Error).message)
}

export const getClientsUsage = async (): Promise<ServiceResponseI<ClientUsageI[]>> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');
        const { data } = await clientsUsageApi.get<ServiceResponseI<ClientUsageI[]>>('', { headers: { 'Authorization': validateToken } });
        return data;
    } catch (err) {
        return handleError(err);
    }
}

interface ContainersParamsI {
    clientId: number | null;
    page: number;
    limit: number;
    process?: ClientUsageProcessT;
    search?: string;
}

// El backend puede devolver { data: [...] } o { data: { dato: [...] } }; se normaliza.
export const getClientUsageContainers = async ({
    clientId, page, limit, process, search
}: ContainersParamsI): Promise<ContainerUsagePageI> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        process && params.append('process', process);
        search && params.append('search', search);
        const { data } = await clientsUsageApi.get<any>(`/${clientId}/containers`, { params, headers: { 'Authorization': validateToken } });
        const body = data?.data ?? data;
        const rows = Array.isArray(body) ? body : (body?.dato ?? body?.data ?? []);
        const meta = Array.isArray(body) ? data : body;
        return {
            data: rows,
            total: meta?.total ?? data?.total ?? rows.length,
            page: meta?.page ?? data?.page ?? page,
            totalPage: meta?.totalPage ?? data?.totalPage ?? 1,
        };
    } catch (err) {
        return handleError(err);
    }
}

interface GroupedParamsI {
    clientId?: number | null;
    page: number;
    limit: number;
    search?: string;
    dateStart?: string;
    dateEnd?: string;
}

export const getClientUsageContainersGrouped = async ({
    clientId, page, limit, search, dateStart, dateEnd
}: GroupedParamsI): Promise<ContainerGroupedUsagePageI> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        clientId && params.append('clientId', clientId.toString());
        search && params.append('search', search);
        dateStart && params.append('dateStart', dateStart);
        dateEnd && params.append('dateEnd', dateEnd);
        const { data } = await clientsUsageApi.get<any>(`/containers-grouped`, { params, headers: { 'Authorization': validateToken } });
        const body = data?.data ?? data;
        const rows = Array.isArray(body) ? body : (body?.dato ?? body?.data ?? []);
        const meta = Array.isArray(body) ? data : body;
        return {
            data: rows,
            total: meta?.total ?? data?.total ?? rows.length,
            page: meta?.page ?? data?.page ?? page,
            totalPage: meta?.totalPage ?? data?.totalPage ?? 1,
        };
    } catch (err) {
        return handleError(err);
    }
}

export const clientsUsageLimitsPut = async (clientId: number, body: ClientUsageLimitsI): Promise<ServiceResponseI<string>> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');
        const { data } = await clientsUsageApi.put<ServiceResponseI<string>>(`/${clientId}/limits`, body, { headers: { 'Authorization': validateToken } });
        return data;
    } catch (err) {
        return handleError(err);
    }
}

export const useClientsUsage = () => {
    const clientsUsageQuery = useQuery(
        ["clients-usage"],
        () => getClientsUsage(),
        { refetchOnWindowFocus: false }
    );
    return { clientsUsageQuery };
}

export const useClientUsageContainersGrouped = (params: GroupedParamsI & { enabled?: boolean }) => {
    const { enabled = true, ...query } = params;
    const groupedQuery = useQuery(
        ["clients-usage-containers-grouped", query],
        () => getClientUsageContainersGrouped(query),
        { refetchOnWindowFocus: false, keepPreviousData: true, enabled }
    );
    return { groupedQuery };
}

export const useClientUsageContainers =(params: ContainersParamsI) => {
    const containersQuery = useQuery(
        ["clients-usage-containers", params],
        () => getClientUsageContainers(params),
        { refetchOnWindowFocus: false, keepPreviousData: true, enabled: params.clientId !== null }
    );
    return { containersQuery };
}
